import { CloseOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Col, Divider, Flex, Row, Select, Space, Tag, Typography } from 'antd';
import { useNavigate } from 'react-router';
import { AssuntoTreeSelect, type SelecaoDeAssuntos } from '@titularis/ui';
import type { ExamModule, QuestionStatus } from '@titularis/api-client';
import { api } from '../../api';
import { SearchBoxCriavel } from '../../components/SearchBoxCriavel';
import {
  MODULO_ASSISTIDO_LABEL,
  MODULO_LABEL,
  MODULOS,
  MODULOS_ASSISTIDOS,
  STATUS_QUESTAO,
  STATUS_QUESTAO_LABEL,
  type ModuloAssistido,
} from '../../dominio';

/**
 * Filtros da listagem de questões, no padrão do app.
 *
 * **Rascunho e aplicado são coisas diferentes.** Os campos ficam num rascunho e
 * só viram consulta no "Buscar". Antes, cada campo mexido disparava uma
 * consulta; no celular, onde os filtros moram numa gaveta por cima da lista,
 * isso é consulta que ninguém vê acontecer - e montar um filtro de três campos
 * custava três buscas para chegar na única que interessa.
 *
 * **Os chips são a memória do que está valendo.** Com a gaveta fechada, os
 * campos escolhidos ficam invisíveis, e "nenhuma questão com esses filtros"
 * sem mostrar quais filtros é um beco sem saída. Cada chip fecha sozinho.
 */

/**
 * Valores reservados do filtro de responsável, para o que ninguém assumiu.
 *
 * **Era um só, `/assistido`, chamado "Cadastro Assistido".** O nome já era
 * mentira quando o Comentário Assistido começou a escrever na mesma fila: o
 * filtro dizia cadastro e devolvia os dois. São dois trabalhos de revisão
 * diferentes - conferir transcrição contra o PDF da prova e conferir argumento
 * contra o acervo -, então são duas opções.
 *
 * A barra os separa dos ids do BetterAuth, que nunca começam com ela.
 */
export const MODULO_NO_FILTRO: Record<ModuloAssistido, string> = {
  cadastro: '/cadastro-assistido',
  comentario: '/comentario-assistido',
};

/** O caminho de volta: do valor do filtro para o módulo. */
export const moduloDoFiltro = (valor: string): ModuloAssistido | undefined =>
  MODULOS_ASSISTIDOS.find((modulo) => MODULO_NO_FILTRO[modulo] === valor);

export interface FiltrosDeQuestoes {
  examId?: string;
  modules: ExamModule[];
  statuses: QuestionStatus[];
  disciplineId?: string;
  assuntos: SelecaoDeAssuntos;
  responsavel: string[];
  fechadas?: string;
}

export const FILTROS_VAZIOS: FiltrosDeQuestoes = {
  examId: undefined,
  modules: [],
  statuses: [],
  disciplineId: undefined,
  assuntos: { subjectIds: [], subjectDetailIds: [] },
  responsavel: [],
  fechadas: undefined,
};

const FECHADAS_LABEL: Record<string, string> = {
  true: 'Fechadas',
  false: 'Falta comentar',
};

/** Quantos filtros estão valendo, para o contador ao lado do botão. */
export function quantosFiltros(filtros: FiltrosDeQuestoes): number {
  return (
    (filtros.examId ? 1 : 0) +
    filtros.modules.length +
    filtros.statuses.length +
    (filtros.disciplineId ? 1 : 0) +
    filtros.assuntos.subjectIds.length +
    filtros.assuntos.subjectDetailIds.length +
    filtros.responsavel.length +
    (filtros.fechadas ? 1 : 0)
  );
}

export function PainelDeFiltros({
  rascunho,
  aplicados,
  nomeDaProva,
  onMudar,
  onAplicar,
  onLimpar,
  onRemover,
  compacto,
}: {
  rascunho: FiltrosDeQuestoes;
  aplicados: FiltrosDeQuestoes;
  /** Nome da prova aplicada, que a tela já carrega para o cabeçalho. */
  nomeDaProva?: string;
  onMudar: (mudancas: Partial<FiltrosDeQuestoes>) => void;
  onAplicar: () => void;
  onLimpar: () => void;
  onRemover: (filtros: FiltrosDeQuestoes) => void;
  compacto: boolean;
}) {
  const navigate = useNavigate();

  const responsaveis = useQuery({
    queryKey: ['question-responsibles'],
    queryFn: () => api.listQuestionResponsibles(),
  });

  const disciplinas = useQuery({
    queryKey: ['disciplines', 'todas'],
    queryFn: () => api.listDisciplines(),
  });

  const temAssunto =
    aplicados.assuntos.subjectIds.length > 0 || aplicados.assuntos.subjectDetailIds.length > 0;

  /*
   * Assunto e delimitação só têm nome dentro da árvore da disciplina. Sem esta
   * consulta, quem chega por link pronto veria "Assunto: 3f2a-..." - o nome só
   * existia na memória de quem tinha acabado de escolher.
   */
  const arvore = useQuery({
    queryKey: ['subject-tree', aplicados.disciplineId],
    queryFn: () => api.subjectTree(aplicados.disciplineId!),
    enabled: Boolean(aplicados.disciplineId) && temAssunto,
  });

  const nomeDoAssunto = (id: string): string | undefined => {
    for (const assunto of arvore.data ?? []) {
      if (assunto.id === id) return assunto.name;
      for (const delimitacao of assunto.details) {
        if (delimitacao.id === id) return delimitacao.name;
      }
    }
    return undefined;
  };

  const nomeDoResponsavel = (id: string): string => {
    const modulo = moduloDoFiltro(id);
    if (modulo) return MODULO_ASSISTIDO_LABEL[modulo];
    const pessoa = responsaveis.data?.items.find((item) => item.id === id);
    return pessoa?.name ?? 'Conta excluída';
  };

  /** Remove um valor e refaz a busca já sem ele. */
  const remover = (ajuste: (filtros: FiltrosDeQuestoes) => FiltrosDeQuestoes) =>
    onRemover(ajuste(aplicados));

  const chips = [
    ...(aplicados.examId
      ? [
          {
            key: 'prova',
            texto: `Prova: ${nomeDaProva ?? '...'}`,
            aoFechar: () => remover((filtros) => ({ ...filtros, examId: undefined })),
          },
        ]
      : []),
    ...aplicados.modules.map((valor) => ({
      key: `modulo:${valor}`,
      texto: `Módulo: ${MODULO_LABEL[valor]}`,
      aoFechar: () =>
        remover((filtros) => ({
          ...filtros,
          modules: filtros.modules.filter((item) => item !== valor),
        })),
    })),
    ...aplicados.statuses.map((valor) => ({
      key: `situacao:${valor}`,
      texto: `Situação: ${STATUS_QUESTAO_LABEL[valor]}`,
      aoFechar: () =>
        remover((filtros) => ({
          ...filtros,
          statuses: filtros.statuses.filter((item) => item !== valor),
        })),
    })),
    ...(aplicados.disciplineId
      ? [
          {
            key: 'disciplina',
            texto: `Disciplina: ${
              disciplinas.data?.find((item) => item.id === aplicados.disciplineId)?.name ?? '...'
            }`,
            aoFechar: () =>
              // Sair da disciplina leva os assuntos junto: eles são dela, e
              // sozinhos filtrariam por uma árvore que não está mais na tela.
              remover((filtros) => ({
                ...filtros,
                disciplineId: undefined,
                assuntos: { subjectIds: [], subjectDetailIds: [] },
              })),
          },
        ]
      : []),
    ...aplicados.assuntos.subjectIds.map((id) => ({
      key: `assunto:${id}`,
      texto: `Assunto: ${nomeDoAssunto(id) ?? '...'}`,
      aoFechar: () =>
        remover((filtros) => ({
          ...filtros,
          assuntos: {
            ...filtros.assuntos,
            subjectIds: filtros.assuntos.subjectIds.filter((item) => item !== id),
          },
        })),
    })),
    ...aplicados.assuntos.subjectDetailIds.map((id) => ({
      key: `delimitacao:${id}`,
      texto: `Delimitação: ${nomeDoAssunto(id) ?? '...'}`,
      aoFechar: () =>
        remover((filtros) => ({
          ...filtros,
          assuntos: {
            ...filtros.assuntos,
            subjectDetailIds: filtros.assuntos.subjectDetailIds.filter((item) => item !== id),
          },
        })),
    })),
    ...aplicados.responsavel.map((id) => ({
      key: `responsavel:${id}`,
      texto: `Responsável: ${nomeDoResponsavel(id)}`,
      aoFechar: () =>
        remover((filtros) => ({
          ...filtros,
          responsavel: filtros.responsavel.filter((item) => item !== id),
        })),
    })),
    ...(aplicados.fechadas
      ? [
          {
            key: 'fechadas',
            texto: `Comentário: ${FECHADAS_LABEL[aplicados.fechadas] ?? aplicados.fechadas}`,
            aoFechar: () => remover((filtros) => ({ ...filtros, fechadas: undefined })),
          },
        ]
      : []),
  ];

  /** Cada filtro numa coluna: três por linha no desktop, uma no celular. */
  const coluna = compacto ? 24 : 8;

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col span={coluna}>
          <SearchBoxCriavel
            value={rascunho.examId}
            onChange={(valor) => onMudar({ examId: valor })}
            queryKey={['exams', 'busca']}
            placeholder="Prova"
            rotulo="Prova"
            rotuloAtual={rascunho.examId === aplicados.examId ? nomeDaProva : undefined}
            buscar={async (termo) => (await api.listExams({ termo })).items}
            acaoExtra={
              <Button
                type="text"
                block
                icon={<PlusOutlined />}
                style={{ textAlign: 'left' }}
                onMouseDown={(evento) => evento.preventDefault()}
                onClick={() => void navigate('/provas/nova')}
              >
                Cadastrar prova
              </Button>
            }
          />
        </Col>
        <Col span={coluna}>
          <Select<ExamModule[]>
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Módulo"
            style={{ width: '100%' }}
            value={rascunho.modules}
            onChange={(valor) => onMudar({ modules: valor })}
            options={MODULOS.map((valor) => ({ value: valor, label: MODULO_LABEL[valor] }))}
          />
        </Col>
        <Col span={coluna}>
          <Select<QuestionStatus[]>
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Situação"
            style={{ width: '100%' }}
            value={rascunho.statuses}
            onChange={(valor) => onMudar({ statuses: valor })}
            options={STATUS_QUESTAO.map((valor) => ({
              value: valor,
              label: STATUS_QUESTAO_LABEL[valor],
            }))}
          />
        </Col>
        <Col span={coluna}>
          <SearchBoxCriavel
            value={rascunho.disciplineId}
            onChange={(valor) =>
              // Trocar a disciplina invalida os assuntos, que são dela.
              onMudar({
                disciplineId: valor,
                assuntos: { subjectIds: [], subjectDetailIds: [] },
              })
            }
            queryKey={['disciplines']}
            placeholder="Disciplina"
            rotulo="Disciplina"
            /*
             * Sem prova escolhida não há contexto que decida o escopo, e
             * "Direito Civil" existe em mais de um (D-18). Sem o escopo no
             * rótulo, a lista seriam entradas idênticas e a escolha, no escuro.
             */
            buscar={async (termo) => {
              const encontradas = await api.listDisciplines({ termo });
              return encontradas.map((disciplina) => ({
                ...disciplina,
                name: `${disciplina.name} · ${disciplina.scopeName}`,
              }));
            }}
          />
        </Col>
        <Col span={compacto ? 24 : 16}>
          <AssuntoTreeSelect
            disciplineIds={rascunho.disciplineId ? [rascunho.disciplineId] : []}
            carregarArvore={(disciplinaId) => api.subjectTree(disciplinaId)}
            nomeDaDisciplina={(id) => disciplinas.data?.find((item) => item.id === id)?.name}
            value={rascunho.assuntos}
            onChange={(selecao) => onMudar({ assuntos: selecao })}
            placeholder={rascunho.disciplineId ? 'Assunto' : 'Escolha a disciplina'}
          />
        </Col>
        <Col span={coluna}>
          <Select<string[]>
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            placeholder="Responsável"
            style={{ width: '100%' }}
            value={rascunho.responsavel}
            loading={responsaveis.isPending}
            onChange={(valor) => onMudar({ responsavel: valor })}
            options={[
              // Só entra o módulo que tem questão sem dono: opção que devolve
              // lista vazia sempre é promessa quebrada.
              ...(responsaveis.data?.assistidos ?? []).map((modulo) => ({
                value: MODULO_NO_FILTRO[modulo],
                label: MODULO_ASSISTIDO_LABEL[modulo],
              })),
              ...(responsaveis.data?.items ?? []).map((pessoa) => ({
                value: pessoa.id,
                label: pessoa.name ?? 'Conta excluída',
              })),
            ]}
          />
        </Col>
        <Col span={coluna}>
          <Select
            allowClear
            placeholder="Comentário"
            style={{ width: '100%' }}
            value={rascunho.fechadas}
            onChange={(valor) => onMudar({ fechadas: valor })}
            options={[
              { value: 'true', label: 'Fechadas' },
              { value: 'false', label: 'Falta comentar' },
            ]}
          />
        </Col>
      </Row>

      {chips.length > 0 ? (
        <>
          <Divider style={{ margin: 0 }} />
          <Space size={[6, 6]} wrap>
            <Typography.Text type="secondary">Filtros aplicados:</Typography.Text>
            {chips.map((chip) => (
              <Tag
                key={chip.key}
                closable
                closeIcon={<CloseOutlined />}
                onClose={(evento) => {
                  evento.preventDefault();
                  chip.aoFechar();
                }}
              >
                {chip.texto}
              </Tag>
            ))}
          </Space>
        </>
      ) : null}

      <Flex justify="flex-end" gap={8} wrap>
        {chips.length > 0 ? <Button onClick={onLimpar}>Limpar</Button> : null}
        <Button type="primary" icon={<SearchOutlined />} onClick={onAplicar}>
          Buscar questões
        </Button>
      </Flex>
    </Space>
  );
}
